// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary
import { useQueryClient } from "@tanstack/react-query";
import Button from "antd/es/button";
import Result from "antd/es/result";
import { useSetAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { RotateCwIcon } from "lucide-react";
import React, {
  type ReactElement,
  Suspense,
  useEffect,
  useRef,
  useState,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";

import { AuthenticationError } from "../errors";
import { getApiEndpoint, getWebUIEndpoint } from "../helpers";
import { HTTP_200_OK } from "../http";
import { ERROR_CODES, type ErrorEnvelope } from "../infra/apiError";
import { fetchWithTimeout } from "../infra/fetchWithTimeout";
import {
  User,
  currentUserAtom,
  isAuthenticatedAtom,
  projectIdAtom,
} from "./atoms";
import { CURRENT_USER_QUERY_KEY, useCurrentUser } from "./useCurrentUser";

const SessionExpiredResult = React.lazy(
  () => import("../components/SessionExpiredResult")
);

/**
 * Bearer-token store. The FastTrack backend authenticates via the
 * `Authorization: Bearer <token>` header (HttpOnly cookies are unusable on
 * the HTTP / cross-origin on-prem deployments this product ships to). We
 * persist in `localStorage` so the session is browser-wide and survives a
 * tab close / browser restart up to the token's lifetime — matching the
 * persistence the previous `bai_session` cookie provided. `sessionStorage`
 * (used briefly in #4216) is per-tab and cleared on close, which logged
 * standalone email/keypair/SSO users out on every new tab and restart (#4236);
 * its only re-auth fallback, the interactive-login probe, works exclusively
 * for users launched from WebUI (who carry an `AIOHTTP_SESSION` cookie).
 *
 * Security tradeoff: the token is JS-readable in either store, but
 * `localStorage` widens the exposure vs `sessionStorage` — it now persists on
 * disk and is shared across every tab, so on a shared / kiosk workstation the
 * token outlives the session up to its lifetime. The shipped defaults do NOT
 * fully contain this: the default CSP (`SECURITY_CSP_DIRECTIVES`) still allows
 * `script-src 'unsafe-inline' 'unsafe-eval'`, which an injected script can use
 * to read and exfiltrate the token, and `TOKEN_LIFETIME_SECONDS` defaults to
 * 24h. Operators on shared/multi-user machines should tighten the CSP and
 * shorten the token lifetime. Note that tightening `SECURITY_CSP_DIRECTIVES`
 * alone is not enough on document responses: the nginx-served document header
 * ships as Report-Only by default, so `SECURITY_CSP_ENFORCE=true` is also
 * required for the policy to block anything.
 * TODO(#4238): harden the default CSP (drop `unsafe-inline`/`unsafe-eval`) and
 * reconsider the 24h default token lifetime now that the token lives on disk.
 * Cross-tab logout/expiry is propagated via the `storage` event (see
 * `AuthProvider`); cross-tab *login* is intentionally not pushed — that matches
 * the prior cookie behavior, where a parked tab only advanced on its next
 * request.
 */
export const AUTH_TOKEN_KEY = "Backend_AI_FastTrack.authToken";

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token: string): void {
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  } catch {
    // Storage write failure (private mode / quota) — auth degrades to the
    // login form on the next request rather than crashing here.
  }
  // A successful sign-in supersedes any prior explicit logout (#4914).
  clearExplicitLogoutMark();
}

export function clearAuthToken(): void {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // ignore
  }
  // Clean up any token a prior (#4216 sessionStorage-based) build may have left.
  try {
    sessionStorage.removeItem(AUTH_TOKEN_KEY);
  } catch {
    // ignore
  }
  // The relayed WebUI session id is part of the same session — drop it too so a
  // logged-out browser can't keep re-validating a stale WebUI session.
  clearWebuiSessionId();
}

/**
 * Stored Webserver `session_id` obtained at interactive login. It is relayed
 * to FastTrack's `webui-session-check` on each poll so the backend can re-run
 * the four-state Webserver probe and propagate an explicit WebUI logout — the
 * role the `AIOHTTP_SESSION` cookie played before #4289. Persisted in
 * `localStorage` (browser-wide, survives reload) alongside the Bearer token,
 * and cleared together with it.
 *
 * Only interactive logins set this; keypair/password/SSO sessions have no
 * WebUI session to propagate, so the absence of this value correctly means
 * "nothing to check" rather than "logged out".
 *
 * Security note: like the Bearer token, this is JS-readable — see the
 * `AUTH_TOKEN_KEY` docblock and TODO(#4238) for the shared CSP/lifetime
 * hardening that bounds the exposure.
 */
export const WEBUI_SESSION_ID_KEY = "Backend_AI_FastTrack.webuiSessionId";

export function getWebuiSessionId(): string | null {
  try {
    return localStorage.getItem(WEBUI_SESSION_ID_KEY);
  } catch {
    return null;
  }
}

export function setWebuiSessionId(sessionId: string): void {
  try {
    localStorage.setItem(WEBUI_SESSION_ID_KEY, sessionId);
  } catch {
    // Storage write failure (private mode / quota) — logout propagation
    // degrades to "not propagated" rather than crashing the login flow.
  }
}

export function clearWebuiSessionId(): void {
  try {
    localStorage.removeItem(WEBUI_SESSION_ID_KEY);
  } catch {
    // ignore
  }
}

/**
 * Explicit-logout mark (#4914). `logout()` cannot end the Webserver's own
 * `AIOHTTP_SESSION` (different origin), so `LoginPage` skips its zero-click
 * probe while this is present — otherwise the probe silently reverts the
 * logout. Set only by explicit logout (expiry teardowns keep silent
 * renewal); cleared by `setAuthToken`/`redirectToInteractiveLogin`.
 * `localStorage` so cross-tab logout stays suppressed too.
 */
export const EXPLICIT_LOGOUT_KEY = "Backend_AI_FastTrack.explicitLogout";

export function markExplicitLogout(): void {
  try {
    // Timestamp value is for debugging only; presence is what matters.
    localStorage.setItem(EXPLICIT_LOGOUT_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

export function clearExplicitLogoutMark(): void {
  try {
    localStorage.removeItem(EXPLICIT_LOGOUT_KEY);
  } catch {
    // ignore
  }
}

export function hasExplicitLogoutMark(): boolean {
  try {
    return localStorage.getItem(EXPLICIT_LOGOUT_KEY) != null;
  } catch {
    return false;
  }
}

/**
 * True when a cross-tab `storage` event means the auth token was cleared
 * (explicit logout or expiry cleanup in another tab). `event.key === null`
 * covers a wholesale `localStorage.clear()`, which also drops the token. Pure
 * so the decision is unit-testable without rendering `AuthProvider`.
 *
 * A foreign-tab *login* (the token being set to a new value) deliberately does
 * NOT match: we don't push a session into other open tabs, mirroring the prior
 * cookie behavior. A parked tab picks the token up on its next request.
 */
export function isAuthTokenClearedEvent(
  event: Pick<StorageEvent, "key" | "newValue">
): boolean {
  const isAuthKey = event.key === null || event.key === AUTH_TOKEN_KEY;
  // Only an absent value means "cleared". `setAuthToken` never writes an empty
  // string and the login paths guard on a truthy token, so `newValue == null`
  // is the sole logout signal — an empty string is not a valid token state.
  return isAuthKey && event.newValue == null;
}

export const getFetchAuthHeaders = () => {
  const headers: {
    [key: string]: string;
  } = {
    "Content-Type": "application/json",
  };
  // Header-based auth: attach the Bearer token when present. No CSRF token is
  // needed — DRF only enforces CSRF for cookie/SessionAuthentication, which
  // FastTrack no longer uses.
  //
  // No `X-BackendAI-Version`: FastTrack's own endpoints (Bearer auth) ignore
  // it, and the `/func/` gateway proxy sets its own version server-side from
  // `ClientConfig.api_version` before signing the upstream Manager request.
  const token = getAuthToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

const buildProxyUrl = (requestPath: string): URL => {
  // /resources/ are static files served directly by the Backend.AI gateway
  // (no auth required) — bypass the /api prefix.
  if (requestPath.startsWith("/resources/")) {
    return new URL(requestPath, getWebUIEndpoint());
  }
  // All other API calls go through /api/ so nginx can cleanly separate
  // them from SPA routes.  Paths that already start with /api/ are
  // passed through unchanged.
  if (!requestPath.startsWith("/api/")) {
    requestPath = `/api${requestPath}`;
  }
  return new URL(requestPath, getApiEndpoint());
};

export function baiFetch(input: string, init?: RequestInit): Promise<Response> {
  const { headers, ...resetInit } = init || {};
  const proxyUrl = buildProxyUrl(input);
  return fetch(proxyUrl, {
    headers: {
      "Content-Type": "application/json",
      ...getFetchAuthHeaders(),
      ...headers,
    },
    mode: "cors",
    ...resetInit,
  });
}

export const resetAuth = () => {
  // Header-based auth: discard the stored Bearer token. The server-side
  // UserToken is revoked separately by the /logout/ endpoint.
  clearAuthToken();
};

interface DirectLoginCredentials {
  username: string;
  password: string;
  domain?: string;
  stoken?: string;
}

interface DirectLoginResponse {
  token?: string;
  message?: string;
  detail?: string;
  user?: {
    email: string;
    is_staff: boolean;
  };
  // 2FA fields (HTTP 428)
  reason?: string;
  type?: string;
  // Failures are enveloped by `envelope_exception_handler`; `code` is what
  // distinguishes a disabled method or an expired password from rejected
  // credentials, since all arrive as 403. Shape is locked to the backend
  // pydantic models by the golden fixtures (see infra/apiError.ts).
  error?: ErrorEnvelope["error"];
}

interface KeypairLoginCredentials {
  access_key: string;
  secret_key: string;
}

/** Response shape of FastTrack's own POST /api/auth/interactive/ endpoint. */
interface InteractiveLoginResponse {
  /** Whether a usable WebUI session was found and exchanged. */
  authenticated: boolean;
  /** The FastTrack Bearer token, present only when `authenticated` is true. */
  token?: string;
}

/** Response shape of POST /api/auth/webui-session-check/. */
interface WebUISessionCheckResponse {
  /**
   * `true` ONLY when the Webserver *explicitly* reports the WebUI session has
   * ended (logged out / expired). A Webserver outage or any ambiguous result
   * yields `false` — the backend fails safe and never reports `session_ended`
   * on uncertainty, so the FastTrack session is kept.
   */
  session_ended: boolean;
}

/**
 * Header that relays the Webserver session id from the browser to FastTrack's
 * own backend. FastTrack re-validates it server-to-server against the
 * Webserver, so interactive login no longer depends on the Webserver's
 * `AIOHTTP_SESSION` cookie reaching FastTrack's (possibly different) origin
 * (#4289).
 */
export const WEBUI_SESSION_HEADER = "X-BackendAI-SessionID";

/** Response shape of the Backend.AI Webserver's POST /server/login-check. */
interface WebServerLoginCheckResponse {
  authenticated: boolean;
  /** Opaque WebUI session id, present only when `authenticated` is true. */
  session_id?: string;
}

/**
 * Why a zero-click interactive-login attempt failed. Collapsing these into a
 * bare "failed" boolean misattributed every failure to CORS on the login page
 * (#4396) — at a customer site the real cause was the Webserver's
 * `Secure`-flagged session cookie being rejected on an HTTP deployment, and
 * the CORS message sent the investigation down the wrong path.
 *
 * - `no_endpoint`: `FRONTEND_RUNTIME_WEBUI_ENDPOINT` is unset or unparseable.
 * - `cors_or_mixed`: the probe failed before any response arrived (fetch
 *   `TypeError`). The browser deliberately hides the cause, so this bucket
 *   covers missing CORS-with-credentials on the Webserver, mixed
 *   HTTP/HTTPS, AND plain unreachability with a fast refusal (service
 *   stopped, DNS failure) — copy and warns must hedge accordingly. Only a
 *   *slow* unreachability (SYN blackhole) lands in `timeout`.
 * - `timeout`: the Webserver did not answer within the probe timeout.
 * - `http_error`: the Webserver answered with a non-2xx status.
 * - `invalid_response`: 2xx but the body was unusable — non-JSON, or JSON
 *   whose `authenticated` field is not an explicit boolean (partial or
 *   proxy-mangled body).
 * - `no_session`: the Webserver answered cleanly but saw no session — the
 *   user is not signed in to WebUI in this browser, OR the session cookie
 *   never reached the Webserver (e.g. `cookie-secure = true` on an HTTP
 *   deployment, host mismatch). The two are indistinguishable from this
 *   origin: the browser does not reveal why it withheld a cookie.
 * - `no_session_id`: authenticated but no `session_id` in the response — the
 *   Webserver predates the header-relay flow (#4291) and must be upgraded.
 * - `relay_failed`: the probe succeeded but FastTrack's own
 *   `/api/auth/interactive/` could not verify the session server-to-server
 *   (gateway endpoint misconfiguration, Manager unreachable).
 */
export type InteractiveLoginFailureReason =
  | "no_endpoint"
  | "cors_or_mixed"
  | "timeout"
  | "http_error"
  | "invalid_response"
  | "no_session"
  | "no_session_id"
  | "relay_failed";

/**
 * Classified result of the browser→Webserver login-check probe. The failure
 * arm carries the probe-level reason; `relay_failed` is excluded because it
 * can only arise in the later FastTrack-side relay step.
 */
export type WebServerLoginCheckResult =
  | { authenticated: true; session_id: string }
  | {
      authenticated: false;
      reason: Exclude<InteractiveLoginFailureReason, "relay_failed">;
    };

/**
 * Outcome of a full `tryInteractiveLogin` attempt (probe + relay).
 * Discriminated on `success` so the compiler enforces that every failure
 * carries a reason — callers never have to invent a fallback.
 */
export type InteractiveLoginAttempt =
  | { success: true }
  | { success: false; reason: InteractiveLoginFailureReason };

/**
 * Normalize a configured Backend.AI Webserver `endpoint` and append `path`,
 * **preserving any sub-path** on the endpoint — e.g. an OEM reverse proxy that
 * fronts the Webserver at `https://corp.example/webui/`. `new URL(absPath,
 * base)` would silently drop `/webui/` and 404, so we append to the normalized
 * base path instead. Shared by `buildWebServerUrl` (the cross-origin probe) and
 * `buildInteractiveLoginUrl` (the redirect fallback) so their normalization
 * can't drift apart.
 *
 * Query / fragment / userinfo are stripped before the path is mutated:
 * - a stray `?foo` / `#frag` on the endpoint must not survive alongside params
 *   a caller appends, nor end up in the Webserver's logs;
 * - `https://user:pass@host/` userinfo would otherwise leak into navigation
 *   history and the Referer surface (cheap defense-in-depth).
 * Trailing slashes are collapsed so `https://corp/webui//` + `/x` does not
 * yield `//x`, which some proxies (nginx `merge_slashes off`) 404.
 *
 * Returns `null` when `endpoint` is unset, unparseable, or not http(s).
 * `path` must start with `/`.
 */
function resolveWebserverUrl(
  endpoint: string | null | undefined,
  path: string
): URL | null {
  if (!endpoint) return null;
  let url: URL;
  try {
    url = new URL(endpoint);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return null;
  }
  url.search = "";
  url.hash = "";
  url.username = "";
  url.password = "";
  url.pathname = url.pathname.replace(/\/+$/, "") + path;
  return url;
}

/**
 * Build a URL on the configured Backend.AI Webserver
 * (`FRONTEND_RUNTIME_WEBUI_ENDPOINT`) for a browser→Webserver cross-origin call.
 * Returns `null` when the endpoint is unset/invalid (the caller then degrades
 * to the password form). `path` must start with `/`.
 */
function buildWebServerUrl(path: string): URL | null {
  return resolveWebserverUrl(getWebUIEndpoint(), path);
}

/**
 * Probe the Backend.AI Webserver's `/server/login-check` **directly from the
 * browser**. This is a deliberate cross-origin call: it targets the
 * Webserver's own origin (`getWebUIEndpoint()`), where the `AIOHTTP_SESSION` cookie
 * is unconditionally in scope, with `credentials: "include"`. The returned
 * `session_id` is then relayed to FastTrack via the `X-BackendAI-SessionID`
 * header — so interactive login works regardless of whether FastTrack and the
 * Webserver share an origin (different port / scheme / host), the regression
 * that broke same-machine domain deployments (#4289).
 *
 * Uses bare `fetch` + a manual AbortController rather than
 * `fetchWithTimeout` / `baiFetch`, which would rebase the URL onto FastTrack's
 * own API origin and silently break the cross-origin call (see
 * `frontend-app/CLAUDE.md`).
 *
 * Requires the Webserver to allow CORS for FastTrack's origin with
 * `Access-Control-Allow-Credentials: true`.
 *
 * Never rejects: any failure (no/invalid endpoint, CORS rejection, transport
 * error, timeout, non-2xx, bad JSON, no session) resolves to
 * `{ authenticated: false, reason }` so the login page falls back to the
 * normal form and can explain *why* (#4396).
 *
 * Operator diagnostics: every anomalous failure emits a `console.warn` naming
 * the reason. `no_session` stays silent here — it fires on every anonymous
 * login-page visit (the normal "render the form" path), so warning would bury
 * the signal; LoginPage warns instead when `no_session` occurs right after a
 * WebUI sign-in round trip, where it IS anomalous. `no_endpoint` also stays
 * silent — LoginPage already renders a dedicated configuration Alert for it.
 */
export async function fetchWebServerLoginCheck(
  timeoutMs = 5_000
): Promise<WebServerLoginCheckResult> {
  const url = buildWebServerUrl("/server/login-check");
  if (!url) return { authenticated: false, reason: "no_endpoint" };
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const isAbortError = (e: unknown): boolean =>
    (e as { name?: unknown } | null)?.name === "AbortError";
  const warnTimedOut = () => {
    // eslint-disable-next-line no-console
    console.warn(
      `[interactive-login] Backend.AI Webserver login-check timed out after ${timeoutMs}ms — ` +
        "the Webserver may be down or overloaded."
    );
  };
  // The timer must stay armed until the BODY read settles, not just the
  // headers: a Webserver/reverse-proxy that returns headers and then stalls
  // the body would otherwise hang `resp.json()` unbounded and pin the login
  // page on its spinner — exactly the broken-deployment class this probe is
  // supposed to fail fast on. Hence one outer `finally` around both awaits.
  try {
    let resp: Response;
    try {
      resp = await fetch(url, {
        method: "POST",
        mode: "cors",
        credentials: "include",
        signal: controller.signal,
      });
    } catch (e) {
      if (isAbortError(e)) {
        warnTimedOut();
        return { authenticated: false, reason: "timeout" };
      }
      // A pre-response failure surfaces as a `TypeError` and the browser
      // hides the cause: a CORS block, mixed HTTP/HTTPS, or a Webserver
      // that is simply down (connection refused). Warn with ALL candidates
      // so operators can diagnose this otherwise-silent fallback without
      // reading backend logs (see docs: "Sign in via Backend.AI").
      // eslint-disable-next-line no-console
      console.warn(
        "[interactive-login] Backend.AI Webserver login-check failed before a " +
          "response arrived — the Webserver may be down/unreachable, or the browser " +
          "blocked the request: check that the Webserver allows CORS for this origin " +
          "with credentials, and that both are served over the same scheme (HTTP/HTTPS).",
        e
      );
      return { authenticated: false, reason: "cors_or_mixed" };
    }
    if (!resp.ok) {
      // eslint-disable-next-line no-console
      console.warn(
        `[interactive-login] Backend.AI Webserver login-check returned HTTP ${resp.status}.`
      );
      return { authenticated: false, reason: "http_error" };
    }
    let data: WebServerLoginCheckResponse;
    try {
      data = (await resp.json()) as WebServerLoginCheckResponse;
    } catch (e) {
      if (isAbortError(e)) {
        // The abort signal fired mid-body — a stalled response, not bad JSON.
        warnTimedOut();
        return { authenticated: false, reason: "timeout" };
      }
      // eslint-disable-next-line no-console
      console.warn(
        "[interactive-login] Backend.AI Webserver login-check returned a non-JSON body."
      );
      return { authenticated: false, reason: "invalid_response" };
    }
    if (data?.authenticated === false) {
      // The common anonymous case — deliberately quiet (see docblock).
      return { authenticated: false, reason: "no_session" };
    }
    if (data?.authenticated !== true) {
      // Well-formed JSON but `authenticated` is not an explicit boolean —
      // missing, null, or a non-boolean like `"false"`/`1` from a mangling
      // proxy. NOT an explicit "no session" and NOT a session either: the
      // `as`-cast above types the field `boolean`, but defending against
      // that cast lying is this function's job. Mirrors the backend's
      // four-state probe, which routes non-boolean values to UNKNOWN; only
      // an explicit `false` may classify as `no_session` and only an
      // explicit `true` may proceed, so real server/proxy misbehavior is
      // neither hidden in the silent anonymous path nor relayed as a
      // session.
      // eslint-disable-next-line no-console
      console.warn(
        "[interactive-login] Backend.AI Webserver login-check returned no explicit " +
          "boolean 'authenticated' field; treating the response as unusable."
      );
      return { authenticated: false, reason: "invalid_response" };
    }
    if (!data.session_id) {
      // eslint-disable-next-line no-console
      console.warn(
        "[interactive-login] Backend.AI Webserver confirmed a session but returned no " +
          "session_id — its version predates the sign-in relay (#4291); upgrade Backend.AI."
      );
      return { authenticated: false, reason: "no_session_id" };
    }
    return { authenticated: true, session_id: data.session_id };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Operator diagnostic for the relay step of interactive login: the probe saw
 * a valid WebUI session, but FastTrack's backend could not verify it. The
 * relay is two server-side hops — re-validating the session against the
 * Webserver (`BACKENDAI_GATEWAY_ENDPOINT /server/login-check`) and then a
 * Manager keypair lookup — so either being misconfigured/unreachable lands
 * here. Naming the server side keeps operators from chasing browser-side
 * cookie/CORS causes (#4396). Note the backend currently collapses "the
 * Webserver explicitly rejected the relayed session" (e.g. WebUI logout in
 * another tab mid-flow) into the same `authenticated: false`, so that rarer
 * cause also surfaces as `relay_failed` — surfacing a reason field from
 * `/api/auth/interactive/` is tracked as a backend follow-up.
 */
function warnRelayFailed(detail: unknown): void {
  // eslint-disable-next-line no-console
  console.warn(
    "[interactive-login] FastTrack could not verify the WebUI session server-to-server " +
      "(/api/auth/interactive/) — check FastTrack's BACKENDAI_GATEWAY_ENDPOINT and that " +
      "the Webserver/Manager are reachable from the FastTrack server.",
    detail
  );
}

interface AuthContextType {
  user: User | null;
  login: (
    callback?: VoidFunction,
    credentials?: KeypairLoginCredentials
  ) => Promise<void>;
  directLogin: (
    credentials: DirectLoginCredentials,
    callback?: VoidFunction
  ) => Promise<DirectLoginResponse | void>;
  /**
   * Zero-click interactive login: read the WebUI session from the Webserver's
   * own origin (cross-origin, `credentials: "include"`) to obtain a
   * `session_id`, then relay it to FastTrack's `/api/auth/interactive/`
   * endpoint via the `X-BackendAI-SessionID` header for a Bearer token (#4289).
   * Resolves to `{ success: true }` when the user was signed in, and
   * `{ success: false, reason }` otherwise so the caller can render the
   * normal login form with an accurate explanation instead of misattributing
   * every failure to CORS (#4396). Never rejects for probe or relay
   * failures — those always resolve with a reason. A rejection can only
   * escape from the post-sign-in bookkeeping (the user IS signed in at that
   * point), which the caller must treat as unevidenced rather than as a
   * relay failure.
   */
  tryInteractiveLogin: () => Promise<InteractiveLoginAttempt>;
  /**
   * Fallback for the zero-click probe: redirects the user to WebUI's
   * interactive-login page so they can sign in there, after which a WebUI
   * session exists and the auto-fire probe (which reads it from the Webserver
   * cross-origin and relays the id to FastTrack) succeeds on return.
   *
   * Callers MUST pass a pre-validated URL — typically the result of
   * `buildInteractiveLoginUrl(getWebUIEndpoint())`. Threading the URL through
   * the call site keeps the visibility predicate (LoginPage decides
   * whether to render the button based on whether the URL is non-null)
   * and the navigation predicate identical, so a misconfigured
   * endpoint cannot land the user on a permanently-spinning button.
   */
  redirectToInteractiveLogin: (interactiveLoginUrl: URL) => void;
  logout: (callback?: VoidFunction) => void;
  /**
   * Re-validate the upstream WebUI session and propagate a WebUI logout.
   * After interactive login the FastTrack Bearer token is decoupled from
   * the WebUI `AIOHTTP_SESSION`; this asks the FastTrack backend to re-probe
   * the WebUI session server-to-server. Resolves to `true` while the
   * FastTrack session should stay (WebUI session valid OR the Webserver was
   * unreachable — fail-safe), and `false` when the WebUI session has
   * explicitly ended, in which case the local session is already cleared.
   * Never rejects.
   */
  tryWebuiSessionCheck: () => Promise<boolean>;
  isInFlightLogin: boolean;
  isInFlightLogout: boolean;
}

// eslint-disable-next-line
const AuthContext = React.createContext<AuthContextType>(null!);

interface AuthProviderProps {
  children: ReactElement;
  onLogin: VoidFunction;
  onLogout: VoidFunction;
  onInvalidToken: VoidFunction;
  invalidTokenFallback?: ReactElement;
}

/**
 * Build the URL that the "Sign in via Backend.AI" fallback redirects to,
 * preserving any sub-path on the configured `FRONTEND_RUNTIME_WEBUI_ENDPOINT`.
 *
 * Returns `null` (no redirect should happen) when:
 * - `endpoint` is empty / undefined.
 * - `endpoint` does not parse as a URL.
 * - `endpoint`'s protocol is not `http:` or `https:`. This is the
 *   open-redirect guard: a misconfigured `runtime-env.js` (typo or
 *   tampered) could otherwise drive the browser to `javascript:` /
 *   `data:` schemes via the `window.location.href` assignment that
 *   consumes this URL.
 *
 * Sub-path preservation matters for OEM deployments behind a corporate
 * reverse proxy that fronts multiple apps on one hostname — e.g.,
 * `https://corp.example/webui/`. `new URL("/interactive-login", base)`
 * would silently drop `/webui/` and 404. We append to the base path
 * instead, normalising the trailing slash.
 */
export function buildInteractiveLoginUrl(
  endpoint: string | null | undefined
): URL | null {
  // Delegates to the shared normalizer (query/fragment/userinfo stripping,
  // trailing-slash collapse, sub-path preservation, http(s) guard) so the
  // redirect target and the cross-origin probe URL stay in lockstep.
  return resolveWebserverUrl(endpoint, "/interactive-login");
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  onLogin,
  onLogout,
  onInvalidToken,
  invalidTokenFallback,
}) => {
  const [isInFlightLogout, setIsInFlightLogout] = useState(false);
  const [isInFlightLogin, setIsInFlightLogin] = useState(false);

  const queryClient = useQueryClient();
  const resetCurrentProjectId = useResetAtom(projectIdAtom);
  const { data: user } = useCurrentUser();
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);
  const { t } = useTranslation("hooks/auth");

  // Sync React Query user data into Jotai so derived atoms
  // (projectListAtom, availableProjectListAtom) stay up-to-date.
  useEffect(() => {
    setCurrentUser(user ?? null);
  }, [user, setCurrentUser]);

  const login: AuthContextType["login"] = async (callback, credentials) => {
    setIsInFlightLogin(true);
    try {
      const accessKey =
        credentials?.access_key ??
        localStorage.getItem("Backend_AI_FastTrack.access_key") ??
        "";
      const secretKey =
        credentials?.secret_key ??
        localStorage.getItem("Backend_AI_FastTrack.secret_key") ??
        "";
      const response = await fetchWithTimeout("/api/auth/keypair/", {
        method: "POST",
        body: JSON.stringify({
          access_key: accessKey,
          secret_key: secretKey,
        }),
      });
      if (response.status !== HTTP_200_OK) {
        resetAuth();
        // Try to extract error message, but guard against non-JSON responses
        // (e.g. nginx 502 HTML) that would throw a parse error.
        let message: string | undefined;
        let code: string | undefined;
        try {
          const body = await response.json();
          // Errors come back enveloped as { error: { code, message } };
          // `message` at the top level is the pre-envelope shape.
          code = body?.error?.code;
          message = body?.error?.message ?? body?.message;
        } catch {
          // non-JSON response — leave message undefined
        }
        if (code === ERROR_CODES.loginMethodDisabled) {
          throw new AuthenticationError("Sign-in method disabled", {
            description:
              message ?? "Keypair sign-in is disabled on this deployment.",
            statusCode: response.status,
          });
        }
        throw new AuthenticationError("Login failed.", {
          description: message,
          statusCode: response.status,
        });
      }
      // Persist the Bearer token from the response body; subsequent requests
      // send it via the `Authorization` header (see `getFetchAuthHeaders`).
      const body: DirectLoginResponse = await response.json();
      if (!body.token) {
        // A 2xx login without a token is a broken contract (#4922) —
        // failing loudly beats a fake authenticated state that 401s on
        // the next request.
        resetAuth();
        throw new AuthenticationError("Login failed.", {
          description:
            body.message ??
            "The server accepted the login but returned no token.",
          statusCode: response.status,
        });
      }
      setAuthToken(body.token);
      // A keypair session owns no WebUI session — drop any id left by a prior
      // interactive login so `tryWebuiSessionCheck` can't relay a stale id and
      // force-log-out this fresh session (#4289).
      clearWebuiSessionId();
      onLogin();
      setIsAuthenticated(true);
      await queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      callback?.();
      resetCurrentProjectId();
    } finally {
      setIsInFlightLogin(false);
    }
  };

  const directLogin: AuthContextType["directLogin"] = async (
    credentials,
    callback
  ) => {
    setIsInFlightLogin(true);
    try {
      const response = await fetchWithTimeout("/api/auth/login/", {
        method: "POST",
        body: JSON.stringify({
          username: credentials.username,
          password: credentials.password,
          domain: credentials.domain ?? "default",
          ...(credentials.stoken ? { stoken: credentials.stoken } : {}),
        }),
      });

      let data: DirectLoginResponse;
      try {
        data = await response.json();
      } catch (parseError) {
        if (response.status === HTTP_200_OK || response.status === 428) {
          // A success/2FA response without JSON is a broken contract —
          // keep failing loudly rather than faking a shape.
          throw parseError;
        }
        // Non-JSON error body (e.g. nginx 502 HTML while Django is down) —
        // classify by status alone, like the keypair flow above.
        data = {};
      }

      // 2FA required
      if (response.status === 428) {
        return data;
      }

      if (response.status === 401 || response.status === 403) {
        // The envelope `error.code` is canonical; `data.reason` is the
        // legacy pre-envelope discriminator, kept as a fallback only until
        // the dual-emit window closes (#5076).
        const errorCode = data.error?.code ?? data.reason;
        // The deployment turned this method off; the credentials were never
        // examined, so "invalid username or password" would be a lie.
        if (errorCode === ERROR_CODES.loginMethodDisabled) {
          throw new AuthenticationError("Sign-in method disabled", {
            description:
              data.error?.message ??
              "Password sign-in is disabled on this deployment.",
            statusCode: response.status,
          });
        }
        if (errorCode === ERROR_CODES.passwordExpired) {
          throw new AuthenticationError("Password expired", {
            description:
              data.error?.message ||
              data.message ||
              "Your password has expired. Please update it in Backend.AI.",
            statusCode: response.status,
          });
        }
        // Raised errors (invalid credentials) carry their message only in
        // the nested envelope; top-level `message` is the hand-built shape.
        throw new AuthenticationError("Login failed", {
          description:
            data.error?.message ||
            data.message ||
            "Invalid username or password.",
          statusCode: response.status,
        });
      }

      if (response.status === 409) {
        throw new AuthenticationError("Active session exists", {
          description:
            data.error?.message ||
            data.detail ||
            "An active session already exists in Backend.AI WebUI.",
          statusCode: 409,
        });
      }

      if (response.status === 429) {
        // The enveloped `Throttled` message names the Retry-After seconds,
        // which the static fallback cannot.
        throw new AuthenticationError("Too many login attempts", {
          description:
            data.error?.message ||
            "You have exceeded the login rate limit. Please try again later.",
          statusCode: 429,
        });
      }

      if (response.status !== HTTP_200_OK) {
        resetAuth();
        throw new AuthenticationError("Login failed.", {
          description:
            data.error?.message ||
            data.message ||
            "Unexpected error during login.",
          statusCode: response.status,
        });
      }

      // Persist the Bearer token from the response body; a token-less 2xx
      // is a broken contract (see keypair login, #4922).
      if (!data.token) {
        resetAuth();
        throw new AuthenticationError("Login failed", {
          description:
            data.message ??
            "The server accepted the login but returned no token.",
          statusCode: response.status,
        });
      }
      setAuthToken(data.token);
      // Password/SSO sessions own no WebUI session — clear any stale
      // interactive-login id so it isn't relayed by `tryWebuiSessionCheck`
      // and used to force-log-out this session (#4289).
      clearWebuiSessionId();
      onLogin();
      setIsAuthenticated(true);
      await queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
      callback?.();
      resetCurrentProjectId();
    } finally {
      setIsInFlightLogin(false);
    }
  };

  const tryInteractiveLogin: AuthContextType["tryInteractiveLogin"] =
    async () => {
      setIsInFlightLogin(true);
      try {
        let token: string;
        // Step 1 — read the WebUI session straight from the Webserver's
        // own origin, where its `AIOHTTP_SESSION` cookie is always in
        // scope (`credentials: "include"`). This sidesteps the
        // cross-origin failure where the cookie never reaches FastTrack's
        // (different) origin (#4289). `{ authenticated: false }` means no
        // usable WebUI session.
        //
        // Short timeout (5s): this runs on login-page mount and gates the
        // login form's disabled state — a slow Webserver must fall back to
        // the password form quickly rather than hang the spinner.
        //
        // Deliberately OUTSIDE the relay_failed try below: the probe never
        // rejects by contract, so if it ever does (a regression), the
        // violation must reach the caller's accuses-nothing rejection
        // handler — not be dressed up as a "gateway misconfigured" relay
        // failure.
        const webuiSession = await fetchWebServerLoginCheck(5_000);
        if (!webuiSession.authenticated) {
          return { success: false, reason: webuiSession.reason };
        }
        const sessionId = webuiSession.session_id;

        // The `relay_failed` catch is scoped to the relay ONLY. The
        // post-sign-in bookkeeping below must not masquerade as a relay
        // failure: once the token is persisted the user IS signed in, and an
        // Alert claiming "gateway endpoint misconfigured" would be the
        // confident-but-wrong copy this classification exists to kill. A
        // bookkeeping throw escapes to the caller's rejection handler, which
        // renders the accuses-nothing copy.
        try {
          // Step 2 — hand the session id to FastTrack's own backend, which
          // re-validates it server-to-server and issues a FastTrack Bearer
          // token. Same-origin to FastTrack, so the Bearer header (not the
          // Webserver cookie) is what authenticates from here on.
          const response = await fetchWithTimeout(
            "/api/auth/interactive/",
            {
              method: "POST",
              headers: { [WEBUI_SESSION_HEADER]: sessionId },
            },
            5_000
          );

          if (!response.ok) {
            // The endpoint is designed to never return a non-2xx for the
            // auto-run path; treat anything else as a relay failure and fall
            // back rather than blocking the login page.
            warnRelayFailed(`HTTP ${response.status}`);
            return { success: false, reason: "relay_failed" };
          }

          const data = (await response.json().catch(() => ({
            authenticated: false,
          }))) as InteractiveLoginResponse;
          if (!data.authenticated || !data.token) {
            // The browser saw a valid WebUI session but FastTrack's backend
            // could not verify it server-to-server — typically a
            // misconfigured gateway endpoint or an unreachable Manager,
            // never a browser problem. Distinct from `no_session` so the
            // login page doesn't send operators chasing cookies or CORS
            // (#4396).
            warnRelayFailed("authenticated:false from /api/auth/interactive/");
            return { success: false, reason: "relay_failed" };
          }
          token = data.token;
        } catch (e) {
          // Transport error / timeout reaching FastTrack's own backend —
          // degrade gracefully to the normal login form.
          warnRelayFailed(e);
          return { success: false, reason: "relay_failed" };
        }

        // A logout may have landed in another tab while the probe was in
        // flight — logout wins; do not persist the token (#4914).
        if (hasExplicitLogoutMark()) {
          return { success: false, reason: "no_session" };
        }

        // Sign-in succeeded — persist the Bearer token (see keypair login)
        // and the WebUI session id so `tryWebuiSessionCheck` can later
        // propagate a WebUI logout.
        setAuthToken(token);
        setWebuiSessionId(sessionId);
        onLogin();
        setIsAuthenticated(true);
        await queryClient.invalidateQueries({
          queryKey: CURRENT_USER_QUERY_KEY,
        });
        resetCurrentProjectId();
        return { success: true };
      } finally {
        setIsInFlightLogin(false);
      }
    };

  // Clear all in-memory auth state. Shared by explicit logout and WebUI
  // logout propagation — `resetAuth` discards the stored Bearer token while
  // the server-side UserToken is revoked separately by the /logout/ and
  // webui-session-check endpoints.
  const clearLocalAuthState = (callback?: VoidFunction) => {
    // Ordering note: the three jotai/React-state writes
    // (``setIsAuthenticated``, ``resetAuth``, ``resetCurrentProjectId``)
    // batch atomically under React 19, so no subscriber sees an
    // intermediate state where ``isAuthenticated=false`` but the
    // persisted projectId is still set. ``queryClient.clear()`` is
    // React Query — separate microtask — but the gating components
    // re-render off the jotai flush first and unmount the
    // project-aware UI before React Query re-notifies. Keep this
    // ordering so the invariant holds.
    setIsAuthenticated(false);
    resetAuth();
    // Clear the persisted project selection so a subsequent user on
    // the same browser (e.g. shared kiosk) does not inherit it.
    resetCurrentProjectId();
    queryClient.clear();
    callback?.();
    onLogout();
  };

  // Keep a fresh reference for the once-subscribed `storage` listener below so
  // it never invokes a stale `clearLocalAuthState` closure.
  const clearLocalAuthStateRef = useRef(clearLocalAuthState);
  useEffect(() => {
    clearLocalAuthStateRef.current = clearLocalAuthState;
  });

  // Cross-tab logout propagation. `localStorage` writes fire a `storage` event
  // in every *other* same-origin tab (never the writer), so a logout/expiry in
  // one tab tears down the rest — restoring the browser-wide teardown the
  // `bai_session` cookie had before #4216 (#4236). Login is intentionally not
  // propagated (see `isAuthTokenClearedEvent`): a parked tab adopts the token
  // on its next request, matching the prior cookie behavior, and avoiding a
  // half-built adoption path (stale WebSocket token, no navigation).
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (isAuthTokenClearedEvent(event)) {
        clearLocalAuthStateRef.current();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const logout = (callback?: VoidFunction) => {
    setIsInFlightLogout(true);
    // Set before any async work so other tabs see the mark first (#4914).
    markExplicitLogout();
    // The server revokes the UserToken on this endpoint; the local Bearer
    // token is discarded in clearLocalAuthState below.
    baiFetch("/logout/", { method: "POST" })
      .catch(() => {
        // Best-effort — proceed with local cleanup even if the server is unreachable
      })
      .finally(() => {
        // The ordering-invariant teardown moved to ``clearLocalAuthState`` so
        // ``tryWebuiSessionCheck`` can reuse it on explicit WebUI-logout
        // propagation without duplicating the state-writes-and-comment block.
        clearLocalAuthState(callback);
        setIsInFlightLogout(false);
      });
  };

  const redirectToInteractiveLogin: AuthContextType["redirectToInteractiveLogin"] =
    (interactiveLoginUrl: URL) => {
      // Clear the auto-fire debounce stamp so the probe re-runs the
      // moment the user returns from WebUI. Without this, returning
      // within 30s would silently fall through to the password form
      // even though a WebUI session now exists on the Webserver.
      //
      // Caveat: this clear is process-wide for the sessionStorage origin.
      // If the user has multiple FastTrack tabs open and clicks the
      // button in tab A, tab B's next refresh also bypasses the
      // debounce. The probe is idempotent and server-to-server so the
      // amplification is bounded, but worth knowing if a future change
      // makes the probe expensive.
      try {
        sessionStorage.removeItem("interactive_login_last_attempt");
      } catch {
        // Storage write failure (private mode) is non-fatal — the debounce
        // expires on its own and the next probe reads the WebUI session anyway.
      }

      // Explicit gesture — re-arm the probe for the return trip (#4914).
      clearExplicitLogoutMark();

      // The `from_interactive=1` marker lets the login page distinguish a
      // first-time direct entry from a return trip after this redirect,
      // so it can surface an explanatory Alert if the auto-fire probe
      // still finds no session (cross-origin / cookie-blocked deployments).
      //
      // TODO: thread the originally-requested deep-link `from` path
      // through the redirect too so a user who hit `/pipelines/123`
      // without a session lands back there after sign-in instead of `/`.
      const callbackUrl = new URL("/login", window.location.origin);
      callbackUrl.searchParams.set("from_interactive", "1");
      interactiveLoginUrl.searchParams.set("name", "Backend.AI FastTrack");
      interactiveLoginUrl.searchParams.set("callback", callbackUrl.href);
      window.location.href = interactiveLoginUrl.href;
    };

  const tryWebuiSessionCheck: AuthContextType["tryWebuiSessionCheck"] =
    async () => {
      let response: Response;
      try {
        // POST to FastTrack's own same-origin endpoint, relaying the stored
        // WebUI session id (from interactive login) via the
        // `X-BackendAI-SessionID` header so the backend can re-probe the WebUI
        // session server-to-server and propagate an explicit WebUI logout
        // (#4289). Keypair/password/SSO sessions have no stored id, so no
        // header is sent and the backend keeps the session (`NO_SESSION`). The
        // endpoint revokes the FastTrack UserToken on an explicit WebUI
        // logout; we discard the stored Bearer token locally below.
        //
        // 5s timeout (not the default 30s): fires on navigation, focus, and
        // a 60s interval, so at OEM scale (1000 tabs × N tabs) a slow
        // Webserver must not pin 30s of pending fetch per tab. Matches the
        // sibling interactive-login probe.
        const sessionId = getWebuiSessionId();
        response = await fetchWithTimeout(
          "/api/auth/webui-session-check/",
          {
            method: "POST",
            ...(sessionId
              ? { headers: { [WEBUI_SESSION_HEADER]: sessionId } }
              : {}),
          },
          5_000
        );
      } catch {
        // Transport error / timeout reaching FastTrack's own backend — the
        // WebUI session state is indeterminate. Fail safe: keep the session.
        return true;
      }

      if (response.status === 401 || response.status === 403) {
        // FastTrack's own session is already invalid (token deleted or
        // expired). Mirror that locally NOW instead of waiting for the
        // error boundary to catch the next API call — otherwise the UI
        // shows zombie authenticated state for one interval tick.
        clearLocalAuthState();
        return false;
      }
      if (!response.ok) {
        // Some other non-2xx — treat as indeterminate, never log the user
        // out on ambiguity. The WebUI session state is unknown to us.
        return true;
      }

      const data = (await response
        .json()
        .catch(() => ({ session_ended: false }))) as WebUISessionCheckResponse;
      if (!data.session_ended) {
        return true;
      }

      // The WebUI session has explicitly ended and the server already revoked
      // the FastTrack UserToken. Mirror that locally.
      clearLocalAuthState();
      return false;
    };

  const value = {
    user: user ?? null,
    login,
    directLogin,
    tryInteractiveLogin,
    redirectToInteractiveLogin,
    logout,
    tryWebuiSessionCheck,
    isInFlightLogin,
    isInFlightLogout,
  };

  return (
    <ErrorBoundary
      onError={(error, info) => {
        if (error instanceof Error && error.message === "InvalidSessionToken") {
          resetAuth();
          onInvalidToken();
        }
      }}
      fallbackRender={({ error, resetErrorBoundary }) => {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        if (errorMessage === "InvalidSessionToken") {
          return (
            <Suspense>
              <SessionExpiredResult
                buttonProps={{
                  onClick: () => {
                    resetErrorBoundary();
                    window.location.href = "/";
                  },
                }}
              />
            </Suspense>
          );
        }
        return (
          <Result
            status="warning"
            title={t("error.Title")}
            extra={
              <Button
                type="primary"
                key="console"
                onClick={() => {
                  resetErrorBoundary();
                  window.location.href = "/";
                }}
                icon={<RotateCwIcon />}
              >
                {t("error.ReloadPage")}
              </Button>
            }
          >
            <div className="desc">
              <h3>{t("error.Details")}</h3>
              <p>
                {import.meta.env.DEV ? errorMessage : t("error.AuthFailed")}
              </p>
            </div>
          </Result>
        );
      }}
    >
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    </ErrorBoundary>
  );
};

export function useAuth() {
  return React.useContext(AuthContext);
}
